import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu';
import {
  Search, MapPin, UserCheck, ChevronDown, ChevronRight, Calendar, FileText, 
  Receipt, Edit2, CreditCard, Send, Eye, Download, MoreVertical, FilePlus, 
  FileOutput, Trash2 as Trash, XCircle, CheckCircle, RotateCcw, Mail, Merge, X, Check
} from 'lucide-react';

export const CRMClientsTab = ({
  prospects,
  clientsCentreFilter, setClientsCentreFilter,
  clientsSearchTerm, setClientsSearchTerm,
  expandedClients, setExpandedClients,
  setSelectedDevis, setSelectedProspect,
  setShowPaiementDialog, setPaiementData,
  setShowAvoirDialog, setAvoirData,
  handlePreviewDevis, handlePreviewFacture, handlePreviewAvoir, handlePreviewEcheancier,
  // Additional required props
  fetchData,
  openDetailsDialog,
  openQuoteDialog,
  setEcheancierCreationData,
  setShowEcheancierCreationDialog
}) => {
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeTarget, setMergeTarget] = useState(null);

  const handleMerge = async () => {
    if (!mergeSource || !mergeTarget) return;
    const sourceP = mergeSource;
    const targetP = mergeTarget;
    const kept = Math.min(sourceP.numero_candidat || 9999, targetP.numero_candidat || 9999);
    try {
      const [tId, sId] = (sourceP.numero_candidat || 9999) <= (targetP.numero_candidat || 9999)
        ? [sourceP.prospect_id, targetP.prospect_id]
        : [targetP.prospect_id, sourceP.prospect_id];
      await axios.post(`/crm/prospects/${tId}/merge/${sId}`);
      toast.success(`Fusion réussie ! N° candidat conservé : ${kept}`);
      setMergeSource(null);
      setMergeSearch('');
      setMergeTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la fusion');
    }
  };

  const mergeTargets = mergeSource ? prospects.filter(p =>
    p.prospect_id !== mergeSource.prospect_id &&
    (mergeSearch === '' || `${p.nom} ${p.prenom} ${p.email}`.toLowerCase().includes(mergeSearch.toLowerCase()))
  ).slice(0, 8) : [];

  return (
    <>
<Card>
  <CardHeader className="pb-2">
    <div className="flex items-center justify-between">
      <CardTitle className="text-lg font-semibold text-[#0F172A]">
        Gestion des Clients et Factures
      </CardTitle>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => {
          // Export Excel - Vue clients uniquement
          const clientsOnly = prospects.filter(p => p.statut === 'client');
          const allDevis = clientsOnly.flatMap(p => 
            (p.devis_historique || [])
              .filter(d => d.statut === 'accepte' || d.statut === 'signe')
              .map(d => ({
                client: `${p?.prenom || ''} ${p?.nom || ''}`,
                email: p?.email || '',
                devis_id: d.devis_id,
                date: d.date_creation?.split('T')[0] || '-',
                montant: d.montant_total || 0,
                statut: d.statut || 'brouillon',
                paiements: (d.paiements || []).reduce((s, pmt) => s + (pmt.montant || 0), 0)
              }))
          );
          const headers = ['Client', 'Email', 'Devis ID', 'Date', 'Montant', 'Statut', 'Payé'];
          const csvContent = [
            headers.join(';'),
            ...allDevis.map(d => [d.client, d.email, d.devis_id, d.date, d.montant, d.statut, d.paiements].join(';'))
          ].join('\n');
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = 'clients_factures_fisl.csv';
          link.click();
          toast.success('Export CSV téléchargé');
        }}>
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </div>
    </div>
  </CardHeader>
  <CardContent>
    {/* Barre de filtres (même structure que Contacts) */}
    <Card className="mb-4">
      <CardContent className="p-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher par nom, prénom ou email..."
              value={clientsSearchTerm}
              onChange={(e) => setClientsSearchTerm(e.target.value)}
              className="pl-10 h-9"
              data-testid="clients-search-input"
            />
          </div>
          <Select value={clientsCentreFilter} onValueChange={setClientsCentreFilter}>
            <SelectTrigger className="w-full sm:w-[180px] h-9" data-testid="clients-centre-filter">
              <MapPin className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Centre" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les centres</SelectItem>
              <SelectItem value="atlantique">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                  Atlantique
                </div>
              </SelectItem>
              <SelectItem value="occitanie">
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 rounded-full bg-orange-500" />
                  Occitanie
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          {(clientsCentreFilter !== 'all' || clientsSearchTerm !== '') && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setClientsCentreFilter('all'); setClientsSearchTerm(''); }}
              className="h-9"
            >
              Effacer filtres
            </Button>
          )}
        </div>
      </CardContent>
    </Card>

    {/* Indicateurs pour les clients basculés */}
    {(() => {
      const allClients = prospects.filter(p => p.statut === 'client');
      // Filtrage par centre et recherche
      const clientsFiltered = allClients.filter(p => {
        const matchesCentre = clientsCentreFilter === 'all' || p.centre === clientsCentreFilter;
        const matchesSearch = clientsSearchTerm === '' || 
          `${p.nom} ${p.prenom} ${p.email}`.toLowerCase().includes(clientsSearchTerm.toLowerCase());
        return matchesCentre && matchesSearch;
      });
      
      // Helper pour vérifier si un devis est validé (statut global OU statuts_par_payeur)
      const isDevisValide = (devis) => {
        // Vérifier le statut global
        if (devis.statut === 'accepte' || devis.statut === 'signe') return true;
        // Vérifier validation_manuelle
        if (devis.validation_manuelle) return true;
        // Vérifier les statuts par payeur (au moins un payeur validé)
        const statutsPayeurs = devis.statuts_par_payeur || {};
        const hasValidatedPayeur = Object.values(statutsPayeurs).some(s => s === 'accepte' || s === 'signe');
        return hasValidatedPayeur;
      };
      
      // Pour CA Prévisionnel: éviter le double comptage des devis multi-payeurs
      const devisValides = [];
      const processedBaseIds = new Set();
      clientsFiltered.forEach(p => {
        (p.devis_historique || []).forEach(d => {
          // Extraire l'ID de base (sans suffixe payeur)
          const parts = (d.devis_id || '').split('-');
          const hasSuffix = parts.length > 1 && ['STG', 'FIN', 'ENT', 'CLI'].includes(parts[parts.length - 1]);
          const baseId = hasSuffix ? parts.slice(0, -1).join('-') : d.devis_id;
          
          // Ne compter qu'une fois par baseId et seulement si validé
          if (!processedBaseIds.has(baseId) && isDevisValide(d)) {
            processedBaseIds.add(baseId);
            devisValides.push(d);
          }
        });
      });
      
      const totalPaiements = clientsFiltered.flatMap(p => (p.devis_historique || []).flatMap(d => d.paiements || [])).reduce((sum, pmt) => sum + (pmt.montant || 0), 0);
      const caPrevi = devisValides.reduce((sum, d) => sum + (d.montant_total || 0), 0);
      
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
            <CardContent className="pt-4">
              <p className="text-xs text-emerald-600 font-medium uppercase tracking-wide">Clients</p>
              <p className="text-2xl font-bold text-emerald-700">{clientsFiltered.length}</p>
              <p className="text-xs text-emerald-500 mt-1">
                {clientsCentreFilter !== 'all' ? `Centre ${clientsCentreFilter}` : 'Basculés depuis Inscrits'}
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="pt-4">
              <p className="text-xs text-green-600 font-medium uppercase tracking-wide">CA Réalisé</p>
              <p className="text-2xl font-bold text-green-700">{totalPaiements.toLocaleString('fr-FR')} €</p>
              <p className="text-xs text-green-500 mt-1">Paiements encaissés</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="pt-4">
              <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">CA Prévisionnel</p>
              <p className="text-2xl font-bold text-blue-700">{caPrevi.toLocaleString('fr-FR')} €</p>
              <p className="text-xs text-blue-500 mt-1">Devis validés</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
            <CardContent className="pt-4">
              <p className="text-xs text-orange-600 font-medium uppercase tracking-wide">Factures</p>
              <p className="text-2xl font-bold text-orange-700">
                {clientsFiltered.reduce((n, p) => n + (p.factures || []).length + (p.devis_historique || []).reduce((m, d) =>
                  m + (d.facture_generee ? 1 : 0) + Object.values(d.factures_par_payeur || {}).filter(f => f?.generee).length, 0), 0)}
              </p>
              <p className="text-xs text-orange-500 mt-1">Générées</p>
            </CardContent>
          </Card>
        </div>
      );
    })()}

    {/* Tableau des clients avec leurs devis/factures */}
    <div className="space-y-4">
      {(() => {
        // Filtrer les clients par centre et recherche
        const allClients = prospects.filter(p => p.statut === 'client');
        const clientsFiltered = allClients.filter(p => {
          const matchesCentre = clientsCentreFilter === 'all' || p.centre === clientsCentreFilter;
          const matchesSearch = clientsSearchTerm === '' || 
            `${p.nom} ${p.prenom} ${p.email}`.toLowerCase().includes(clientsSearchTerm.toLowerCase());
          return matchesCentre && matchesSearch;
        });
        
        if (clientsFiltered.length === 0) {
          return (
            <Card className="p-8 text-center text-slate-500">
              <UserCheck className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>Aucun client pour le moment</p>
              <p className="text-xs text-slate-400 mt-1">
                {clientsCentreFilter !== 'all' 
                  ? `Aucun client trouvé pour le centre ${clientsCentreFilter}`
                  : 'Les clients apparaissent ici après avoir été basculés depuis le tableau "Inscrits"'
                }
              </p>
            </Card>
          );
        }
        
        return clientsFiltered.map((client, clientIndex) => {
          const allDevis = client.devis_historique || [];
          const isExpanded = expandedClients[client.prospect_id] !== false; // Déplié par défaut
          const totalDevis = allDevis.length;
          const totalFactures = allDevis.filter(d => d.facture_generee).length;
          const totalAvoirs = allDevis.reduce((sum, d) => sum + (d.avoirs?.length || 0), 0);
          
          // Calcul des montants par payeur pour le résumé
          // SEULES LES FACTURES GÉNÉRÉES comptent pour le "dû"
          // Architecture multi-payeur: toutes les données (paiements, avoirs, factures_par_payeur) 
          // sont stockées dans le devis principal (-STG), les autres devis (-ENT, -FIN) sont des copies
          const resumePayeurs = {};
          const processedBaseIds = new Set(); // Pour éviter les doublons
          
          // Collecter TOUS les paiements, avoirs et factures_par_payeur de tous les devis
          const allPaiements = [];
          const allAvoirs = [];
          const allFacturesParPayeur = {};
          
          // ID de base d'un devis (sans suffixe payeur -STG/-ENT/-FIN/-CLI)
          const baseOf = (id) => {
            const parts = (id || '').split('-');
            return parts.length > 1 && ['STG', 'FIN', 'ENT', 'CLI'].includes(parts[parts.length - 1]) ? parts.slice(0, -1).join('-') : id;
          };
          allDevis.forEach(d => {
            const base = baseOf(d.devis_id);
            (d.paiements || []).forEach(p => allPaiements.push({...p, base}));
            (d.avoirs || []).forEach(a => allAvoirs.push({...a, base}));
            // Merger les factures_par_payeur des devis d'un même groupe (même ID de base)
            Object.entries(d.factures_par_payeur || {}).forEach(([key, val]) => {
              if (val?.generee) allFacturesParPayeur[`${base}|${key}`] = val;
            });
          });
          
          allDevis.forEach(devis => {
            const fact = devis.facturation || {};
            
            // Extraire l'ID de base du devis (sans suffixe payeur)
            const devisIdParts = (devis.devis_id || '').split('-');
            const hasPayeurSuffix = devisIdParts.length > 1 && ['STG', 'FIN', 'ENT', 'CLI'].includes(devisIdParts[devisIdParts.length - 1]);
            const baseDevisId = hasPayeurSuffix ? devisIdParts.slice(0, -1).join('-') : devis.devis_id;
            const currentPayeurSuffix = hasPayeurSuffix ? devisIdParts[devisIdParts.length - 1] : null;
            
            // Éviter le double comptage : ne traiter que le devis -STG ou le devis sans suffixe
            // Les devis -ENT et -FIN sont des doublons avec les mêmes données de facturation
            if (currentPayeurSuffix && currentPayeurSuffix !== 'STG') {
              return; // Skip les devis -ENT et -FIN pour éviter double comptage
            }
            
            // Vérifier si on a déjà traité ce baseDevisId
            if (processedBaseIds.has(baseDevisId)) return;
            processedBaseIds.add(baseDevisId);
            
            // Helper pour vérifier si une facture est générée pour ce payeur
            // Utilise allFacturesParPayeur qui agrège toutes les sources
            const isFactureGeneree = (payeurKey) => {
              return allFacturesParPayeur[`${baseDevisId}|${payeurKey}`]?.generee === true || devis.facture_generee === true;
            };
            
            // Chercher les paiements par payeur_key (tous les devis confondus)
            const getPaiementsForPayeur = (payeurKey) => {
              return allPaiements.filter(p => p.base === baseDevisId && p.payeur_key === payeurKey);
            };
            
            const getAvoirsForPayeur = (payeurKey) => {
              return allAvoirs.filter(a => a.base === baseDevisId && a.payeur_key === payeurKey);
            };
            
            // Traiter TOUS les payeurs depuis ce devis principal
            
            // Vérifier si au moins un payeur spécifique est actif
            const hasSpecificPayeur = (fact.stagiaire?.actif && fact.stagiaire?.montant > 0) || 
                                       (fact.entreprise?.actif && fact.entreprise?.montant > 0) || 
                                       (fact.financeur?.actif && fact.financeur?.montant > 0);
            
            // Si aucun payeur spécifique, utiliser le payeur CLI (Client direct)
            if (!hasSpecificPayeur) {
              const montant = devis.montant_total || 0;
              if (!resumePayeurs.CLI) resumePayeurs.CLI = { type: 'Client', du: 0, paye: 0 };
              resumePayeurs.CLI.paye += getPaiementsForPayeur('CLI').reduce((s, p) => s + (p.montant || 0), 0);
              // Compter aussi les paiements sans payeur_key
              resumePayeurs.CLI.paye += allPaiements.filter(p => p.base === baseDevisId && !p.payeur_key).reduce((s, p) => s + (p.montant || 0), 0);
              if (isFactureGeneree('CLI') || devis.facture_generee) {
                const avoirsCLI = getAvoirsForPayeur('CLI').reduce((s, a) => s + (a.montant || 0), 0);
                const avoirsNull = allAvoirs.filter(a => a.base === baseDevisId && !a.payeur_key).reduce((s, a) => s + (a.montant || 0), 0);
                resumePayeurs.CLI.du += (montant - avoirsCLI - avoirsNull);
              }
            }
            
            // Stagiaire
            if (fact.stagiaire?.actif && fact.stagiaire?.montant > 0) {
              const montant = fact.stagiaire.montant;
              if (!resumePayeurs.STG) resumePayeurs.STG = { type: 'Stagiaire', du: 0, paye: 0 };
              resumePayeurs.STG.paye += getPaiementsForPayeur('STG').reduce((s, p) => s + (p.montant || 0), 0);
              if (isFactureGeneree('STG')) {
                const avoirsSTG = getAvoirsForPayeur('STG').reduce((s, a) => s + (a.montant || 0), 0);
                resumePayeurs.STG.du += (montant - avoirsSTG);
              }
            }
            
            // Entreprise
            if (fact.entreprise?.actif && fact.entreprise?.montant > 0) {
              const montant = fact.entreprise.montant;
              if (!resumePayeurs.ENT) resumePayeurs.ENT = { type: 'Entreprise', du: 0, paye: 0 };
              resumePayeurs.ENT.paye += getPaiementsForPayeur('ENT').reduce((s, p) => s + (p.montant || 0), 0);
              if (isFactureGeneree('ENT')) {
                const avoirsENT = getAvoirsForPayeur('ENT').reduce((s, a) => s + (a.montant || 0), 0);
                resumePayeurs.ENT.du += (montant - avoirsENT);
              }
            }
            
            // Financeur
            if (fact.financeur?.actif && fact.financeur?.montant > 0) {
              const montant = fact.financeur.montant;
              if (!resumePayeurs.FIN) resumePayeurs.FIN = { type: 'Financeur', du: 0, paye: 0 };
              resumePayeurs.FIN.paye += getPaiementsForPayeur('FIN').reduce((s, p) => s + (p.montant || 0), 0);
              if (isFactureGeneree('FIN')) {
                const avoirsFIN = getAvoirsForPayeur('FIN').reduce((s, a) => s + (a.montant || 0), 0);
                resumePayeurs.FIN.du += (montant - avoirsFIN);
              }
            }
          });
          
          return (
            <Card key={client.prospect_id} className={`overflow-hidden ${clientIndex % 2 === 0 ? 'border-l-4 border-l-blue-400' : 'border-l-4 border-l-emerald-400'}`} data-testid={`client-card-${client.prospect_id}`}>
              {/* En-tête client avec flèche de déploiement */}
              <div 
                className={`p-4 border-b flex items-center justify-between cursor-pointer transition-colors ${
                  clientIndex % 2 === 0 
                    ? 'bg-blue-50/50 hover:bg-blue-50' 
                    : 'bg-emerald-50/50 hover:bg-emerald-50'
                }`}
                onClick={() => setExpandedClients(prev => ({...prev, [client.prospect_id]: !isExpanded}))}
              >
                <div className="flex items-center gap-4">
                  {/* Flèche de déploiement */}
                  <div className="text-slate-400">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5" />
                    ) : (
                      <ChevronRight className="h-5 w-5" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">
                      {client.numero_candidat && (
                        <span className="text-xs font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded mr-2">N°{String(client.numero_candidat).padStart(4, '0')}</span>
                      )}
                      {client.prenom} {client.nom}
                    </p>
                    <p className="text-sm text-slate-500">{client.email}</p>
                    {client.telephone && <p className="text-xs text-slate-400">{client.telephone}</p>}
                  </div>
                  <Badge
                    variant="outline"
                    className={client.centre === 'occitanie'
                      ? 'bg-orange-100 text-orange-700 border-orange-300'
                      : 'bg-blue-100 text-blue-700 border-blue-300'
                    }
                  >
                    {client.centre === 'occitanie' ? 'Occitanie' : 'Atlantique'}
                  </Badge>
                </div>
                
                {/* Résumé financier global - TOUJOURS VISIBLE sur le bandeau */}
                {(() => {
                  const totalPaye = Object.values(resumePayeurs).reduce((s, d) => s + d.paye, 0);
                  const totalDu = Object.values(resumePayeurs).reduce((s, d) => s + d.du, 0);
                  const totalRestant = totalDu - totalPaye;
                  const hasData = Object.keys(resumePayeurs).length > 0;
                  
                  return hasData ? (
                    <div className="flex items-center gap-3 mr-4" onClick={(e) => e.stopPropagation()}>
                      {/* Total global */}
                      <div className="text-sm px-4 py-2 rounded-lg border bg-white border-slate-200 shadow-sm" data-testid={`client-bandeau-totals-${client.prospect_id}`}>
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Payé</p>
                            <p className="text-sm font-bold text-emerald-600">{totalPaye.toLocaleString('fr-FR')} €</p>
                          </div>
                          <div className="w-px h-8 bg-slate-200" />
                          <div className="text-center">
                            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Restant dû</p>
                            <p className={`text-sm font-bold ${totalRestant > 0 ? 'text-red-600' : 'text-green-600'}`}>{totalRestant.toLocaleString('fr-FR')} €</p>
                          </div>
                        </div>
                      </div>
                      {/* Détail par payeur */}
                      {Object.entries(resumePayeurs).map(([key, data]) => {
                        const restantDu = data.du - data.paye;
                        return (
                        <div key={key} className={`text-xs px-3 py-1.5 rounded-lg border ${
                          key === 'STG' ? 'bg-green-50 border-green-200' :
                          key === 'FIN' ? 'bg-purple-50 border-purple-200' :
                          'bg-blue-50 border-blue-200'
                        }`}>
                          <span className={`font-medium ${
                            key === 'STG' ? 'text-green-700' :
                            key === 'FIN' ? 'text-purple-700' :
                            'text-blue-700'
                          }`}>{data.type}</span>
                          <div className="flex gap-2 mt-0.5">
                            <span className="text-emerald-600">Payé: <strong>{data.paye.toLocaleString('fr-FR')} €</strong></span>
                            <span className={restantDu > 0 ? 'text-red-600' : 'text-green-600'}>
                              Restant: <strong>{restantDu.toLocaleString('fr-FR')} €</strong>
                            </span>
                          </div>
                        </div>
                      );})}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-slate-500 mr-4">
                      <span className="bg-slate-200 px-2 py-0.5 rounded">{totalDevis} devis</span>
                    </div>
                  );
                })()}
                
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button variant="outline" size="sm" onClick={() => openDetailsDialog(client)}>
                    <Eye className="h-4 w-4 mr-1" />
                    Détails
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openQuoteDialog(client)}>
                    <FilePlus className="h-4 w-4 mr-1" />
                    Nouveau devis
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setMergeSource(client); setMergeSearch(''); }}
                    data-testid={`merge-client-${client.prospect_id}`}
                  >
                    <Merge className="h-4 w-4 mr-1" />
                    Fusionner
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-amber-700 border-amber-300 hover:bg-amber-50"
                    data-testid={`repasser-candidat-${client.prospect_id}`}
                    onClick={async () => {
                      if (!window.confirm(`Repasser ${client.prenom} ${client.nom} en candidat ? Il retournera dans l'onglet « Candidats ».`)) return;
                      try {
                        await axios.put(`/crm/prospects/${client.prospect_id}`, { statut: 'candidat_inscrit' });
                        toast.success(`${client.prenom} ${client.nom} repassé en candidat`);
                        fetchData();
                      } catch (error) {
                        toast.error('Erreur lors de la modification');
                      }
                    }}
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Repasser en candidat
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid={`retirer-client-${client.prospect_id}`}
                    onClick={async () => {
                      if (window.confirm(`Retirer ${client.prenom} ${client.nom} des clients ? Il retournera dans l'onglet ${client.candidat_user_id ? '« Candidats »' : '« Prospects »'}.`)) {
                        try {
                          await axios.put(`/crm/prospects/${client.prospect_id}`, { statut: 'nouveau' });
                          toast.success(`${client.prenom} ${client.nom} retiré des clients`);
                          fetchData();
                        } catch (error) {
                          toast.error('Erreur lors de la modification');
                        }
                      }
                    }}
                  >
                    <Trash className="h-4 w-4 mr-1" />
                    Retirer
                  </Button>
                </div>
              </div>
              
              {/* Liste des devis/factures - dépliable */}
              {isExpanded && (
                <>
                  {allDevis.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">
                      <FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                      <p className="text-sm">Aucun devis pour ce client</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {/* Regrouper les devis par ID de base (sans suffixe payeur) */}
                      {(() => {
                        // Grouper les devis par leur ID de base
                        const devisGroups = {};
                        allDevis.forEach(devis => {
                          const devisIdParts = (devis.devis_id || '').split('-');
                          const hasPayeurSuffix = devisIdParts.length > 1 && ['STG', 'FIN', 'ENT', 'CLI'].includes(devisIdParts[devisIdParts.length - 1]);
                          // ID de base = tout sauf le dernier segment si c'est un suffixe payeur
                          const baseId = hasPayeurSuffix ? devisIdParts.slice(0, -1).join('-') : devis.devis_id;
                          
                          if (!devisGroups[baseId]) {
                            devisGroups[baseId] = {
                              baseId,
                              devis: devis, // Garder le premier devis comme référence
                              payeurs: [],
                              allDevisIds: []
                            };
                          }
                          
                          devisGroups[baseId].allDevisIds.push(devis.devis_id);
                          
                          // Extraire les payeurs de ce devis
                          const fact = devis.facturation || {};
                          const payeurSuffix = hasPayeurSuffix ? devisIdParts[devisIdParts.length - 1] : null;
                          
                          // Ajouter les payeurs si actifs
                          if (fact.stagiaire?.actif && fact.stagiaire?.montant > 0 && (!payeurSuffix || payeurSuffix === 'STG')) {
                            // Vérifier si ce payeur n'est pas déjà ajouté
                            if (!devisGroups[baseId].payeurs.find(p => p.key === 'STG')) {
                              devisGroups[baseId].payeurs.push({
                                type: 'Stagiaire',
                                key: 'STG',
                                nom: `${client.prenom} ${client.nom}`,
                                montant: fact.stagiaire.montant,
                                adresse: fact.stagiaire.adresse || client.adresse,
                                code_postal: fact.stagiaire.code_postal || client.code_postal,
                                ville: fact.stagiaire.ville || client.ville,
                                devisId: devis.devis_id,
                                devisData: devis
                              });
                            }
                          }
                          if (fact.financeur?.actif && fact.financeur?.montant > 0 && (!payeurSuffix || payeurSuffix === 'FIN')) {
                            if (!devisGroups[baseId].payeurs.find(p => p.key === 'FIN')) {
                              devisGroups[baseId].payeurs.push({
                                type: 'Financeur',
                                key: 'FIN',
                                nom: fact.financeur.nom || 'Financeur',
                                montant: fact.financeur.montant,
                                adresse: fact.financeur.adresse,
                                code_postal: fact.financeur.code_postal,
                                ville: fact.financeur.ville,
                                devisId: devis.devis_id,
                                devisData: devis
                              });
                            }
                          }
                          if (fact.entreprise?.actif && fact.entreprise?.montant > 0 && (!payeurSuffix || payeurSuffix === 'ENT')) {
                            if (!devisGroups[baseId].payeurs.find(p => p.key === 'ENT')) {
                              devisGroups[baseId].payeurs.push({
                                type: 'Entreprise',
                                key: 'ENT',
                                nom: fact.entreprise.nom || 'Entreprise',
                                montant: fact.entreprise.montant,
                                adresse: fact.entreprise.adresse,
                                code_postal: fact.entreprise.code_postal,
                                ville: fact.entreprise.ville,
                                devisId: devis.devis_id,
                                devisData: devis
                              });
                            }
                          }
                        });
                        
                        // Afficher chaque groupe de devis
                        return Object.values(devisGroups).map(group => {
                          const devis = group.devis;
                          const payeurs = group.payeurs.length > 0 ? group.payeurs : [{
                            type: 'Client',
                            key: 'CLI',
                            nom: `${client.prenom} ${client.nom}`,
                            montant: devis.montant_total,
                            adresse: client.adresse,
                            code_postal: client.code_postal,
                            ville: client.ville,
                            devisId: devis.devis_id,
                            devisData: devis
                          }];
                        
                        const partage = !!devis.devis_partage;
                        const supprimerDevis = async () => {
                          const signe = devis.statut === 'signe' || devis.statut === 'accepte';
                          if (!window.confirm(`${signe ? 'Ce devis est signé. ' : ''}Supprimer définitivement ce devis ? Les factures liées seront aussi supprimées.`)) return;
                          try {
                            await axios.delete(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}`);
                            toast.success('Devis supprimé'); fetchData();
                          } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
                        };
                        return (
                      <div key={devis.devis_id}>
                        {partage && (
                          <div className="mx-3 mt-3 px-4 py-2 rounded-lg bg-indigo-50 border border-indigo-200 flex flex-wrap items-center justify-between gap-2" data-testid={`devis-partage-${devis.devis_id}`}>
                            <div className="flex items-center gap-2 text-sm">
                              <Badge className="bg-indigo-500">Devis partagé</Badge>
                              <span className="font-medium text-slate-700">{devis.devis_numero || `#${devis.devis_id}`}</span>
                              <span className="font-bold text-slate-900">{(devis.montant_total || 0).toLocaleString('fr-FR')} €</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handlePreviewDevis(client.prospect_id, devis.devis_id)} data-testid={`preview-devis-partage-${devis.devis_id}`}>
                                <Eye className="h-3 w-3 mr-1" /> Voir
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50" onClick={supprimerDevis} data-testid={`delete-devis-partage-${devis.devis_id}`}>
                                <Trash className="h-3 w-3 mr-1" /> Supprimer
                              </Button>
                            </div>
                          </div>
                        )}
                        {/* Lignes par payeur - CHAQUE PAYEUR EST 100% INDEPENDANT */}
                        {payeurs.map((payeur) => {
                          const payeurKey = payeur.key;
                          
                          // Construire l'ID du devis spécifique à ce payeur
                          // Si le devis_id a déjà un suffixe (-STG, -ENT, -FIN), le remplacer
                          const devisIdParts = (devis.devis_id || '').split('-');
                          const hasSuffix = devisIdParts.length > 1 && ['STG', 'FIN', 'ENT', 'CLI'].includes(devisIdParts[devisIdParts.length - 1]);
                          const baseDevisId = hasSuffix ? devisIdParts.slice(0, -1).join('-') : devis.devis_id;
                          const devisPayeurId = `${baseDevisId}-${payeurKey}`;
                          
                          // Facture générée pour CE payeur uniquement
                          const payeurFactureGeneree = devis.factures_par_payeur?.[payeurKey]?.generee === true;
                          const factureAutreSysteme = (client.factures || []).some(f => f.devis_id === devisPayeurId);
                          const payeurFactureNumero = devis.factures_par_payeur?.[payeurKey]?.numero;
                          const payeurFactureDate = devis.factures_par_payeur?.[payeurKey]?.date;
                          
                          // Statut spécifique à CE payeur uniquement, fallback au statut global du devis
                          const payeurStatut = devis.statuts_par_payeur?.[payeurKey] || devis.statut || 'brouillon';
                          
                          // Paiements spécifiques à CE payeur uniquement (STRICT - pas de fallback)
                          const payeurPaiements = (devis.paiements || []).filter(p => p.payeur_key === payeurKey);
                          const payeurTotalPaye = payeurPaiements.reduce((sum, pmt) => sum + (pmt.montant || 0), 0);
                          
                          // Avoirs spécifiques à CE payeur uniquement (STRICT - pas de fallback)
                          const payeurAvoirs = (devis.avoirs || []).filter(a => a.payeur_key === payeurKey);
                          const payeurTotalAvoirs = payeurAvoirs.reduce((sum, av) => sum + (av.montant || 0), 0);
                          
                          // Calcul du restant dû pour CE payeur
                          const payeurRestantDu = payeur.montant - payeurTotalAvoirs - payeurTotalPaye;
                          
                          return (
                          <div 
                            key={devisPayeurId}
                            className="rounded-lg border border-slate-200 m-3 overflow-hidden"
                          >
                            {/* === DEVIS === */}
                            <div className={`px-4 py-3 ${
                              payeur.type === 'Stagiaire' ? 'bg-green-50 border-l-4 border-green-500' :
                              payeur.type === 'Entreprise' ? 'bg-blue-50 border-l-4 border-blue-500' :
                              payeur.type === 'Financeur' ? 'bg-purple-50 border-l-4 border-purple-500' :
                              'bg-slate-50 border-l-4 border-slate-300'
                            }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Badge variant="outline" className={
                                    payeur.type === 'Stagiaire' ? 'bg-green-100 text-green-700' :
                                    payeur.type === 'Entreprise' ? 'bg-blue-100 text-blue-700' :
                                    payeur.type === 'Financeur' ? 'bg-purple-100 text-purple-700' :
                                    'bg-slate-100'
                                  }>
                                    {partage ? `Part ${payeur.type}` : `Devis ${payeur.type}`}
                                  </Badge>
                                  <span className="text-sm font-medium text-slate-700">{payeur.nom}</span>
                                  <span className="text-xs text-slate-400">#{devis.devis_id?.slice(0, 8)}-{payeurKey}</span>
                                  <span className="text-xs text-slate-400">
                                    {devis.date_creation ? new Date(devis.date_creation).toLocaleDateString('fr-FR') : ''}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-lg font-bold text-slate-900">{payeur.montant?.toLocaleString('fr-FR')} €</span>
                                  <Badge className={
                                    payeurStatut === 'accepte' || payeurStatut === 'signe' ? 'bg-green-100 text-green-700' :
                                    payeurStatut === 'envoye' ? 'bg-blue-100 text-blue-700' :
                                    'bg-slate-100 text-slate-600'
                                  }>
                                    {payeurStatut === 'accepte' ? 'Validé' :
                                     payeurStatut === 'signe' ? 'Signé' : 
                                     payeurStatut === 'envoye' ? 'Envoyé' : 'Brouillon'}
                                  </Badge>
                                </div>
                              </div>
                              
                              {/* Actions devis */}
                              <div className="flex items-center gap-2 mt-2">
                                {!partage && (<>
                                <Button variant="outline" size="sm" className="h-7 text-xs"
                                  onClick={() => handlePreviewDevis(client.prospect_id, devisPayeurId)}
                                  data-testid={`preview-devis-${devisPayeurId}`}
                                >
                                  <Eye className="h-3 w-3 mr-1" /> Voir
                                </Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={async () => {
                                  try {
                                    const response = await axios.get(`/crm/prospects/${client.prospect_id}/devis/${devisPayeurId}/download`, { responseType: 'blob' });
                                    const url = window.URL.createObjectURL(new Blob([response.data]));
                                    const link = document.createElement('a');
                                    link.href = url;
                                    link.setAttribute('download', `${client.nom || ''}_${client.prenom || ''}_devis.pdf`);
                                    document.body.appendChild(link); link.click(); link.remove();
                                    toast.success('Devis téléchargé');
                                  } catch (error) { toast.error('Erreur lors du téléchargement'); }
                                }}>
                                  <Download className="h-3 w-3 mr-1" /> PDF
                                </Button>
                                </>)}
                                
                                {/* Transformer en facture si validé */}
                                {(payeurStatut === 'accepte' || payeurStatut === 'signe') && !payeurFactureGeneree && !factureAutreSysteme && (
                                  <Button variant="default" size="sm" className="h-7 text-xs bg-orange-500 hover:bg-orange-600" onClick={async () => {
                                    try {
                                      await axios.post(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/generate-facture?payeur_key=${payeurKey}`);
                                      toast.success(`Facture ${payeur.type} générée`);
                                      fetchData();
                                    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
                                  }}>
                                    <FilePlus className="h-3 w-3 mr-1" /> Transformer en facture
                                  </Button>
                                )}
                                
                                {/* Menu secondaire */}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="h-7 w-7 p-0">
                                      <MoreVertical className="h-3.5 w-3.5" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => { setSelectedProspect(client); openQuoteDialog(client); }}>
                                      <Edit2 className="h-3 w-3 mr-2" /> Modifier le devis
                                    </DropdownMenuItem>
                                    {payeurStatut !== 'accepte' && payeurStatut !== 'signe' && (
                                      <DropdownMenuItem onClick={async () => {
                                        try {
                                          await axios.patch(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/validate?payeur_key=${payeurKey}`);
                                          toast.success(`Devis ${payeur.type} validé !`); fetchData();
                                        } catch (error) { toast.error('Erreur'); }
                                      }}>
                                        <CheckCircle className="h-3 w-3 mr-2" /> Valider
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={async () => {
                                      try {
                                        await axios.post(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/send?payeur_key=${payeurKey}`);
                                        toast.success(`Devis envoyé`); fetchData();
                                      } catch (error) { toast.error('Erreur'); }
                                    }}>
                                      <Mail className="h-3 w-3 mr-2" /> {payeurStatut === 'envoye' ? 'Renvoyer' : 'Envoyer'}
                                    </DropdownMenuItem>
                                    {!partage && (
                                      <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem className="text-red-600" onClick={supprimerDevis}>
                                          <Trash className="h-3 w-3 mr-2" /> Supprimer le devis
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </div>
                            
                            {/* === FACTURE === */}
                            {payeurFactureGeneree && (
                              <div className="border-t border-slate-200">
                                {/* En-tête facture */}
                                <div className="px-4 py-3 bg-white">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <Badge className="bg-green-100 text-green-700 border-green-200">
                                        <Receipt className="h-3 w-3 mr-1" /> Facture
                                      </Badge>
                                      <span className="text-xs text-slate-500">
                                        N° {payeurFactureNumero || `${devis.devis_id?.slice(0, 8)}-${payeurKey}`}
                                      </span>
                                      <span className="text-xs text-slate-400">
                                        {payeurFactureDate ? new Date(payeurFactureDate).toLocaleDateString('fr-FR') : ''}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-lg font-bold text-slate-900">{payeur.montant?.toLocaleString('fr-FR')} €</span>
                                      {payeurRestantDu <= 0 ? (
                                        <Badge className="bg-green-100 text-green-700">Payée</Badge>
                                      ) : payeurTotalPaye > 0 ? (
                                        <Badge className="bg-blue-100 text-blue-700">Partiel</Badge>
                                      ) : (
                                        <Badge className="bg-red-100 text-red-700">À payer</Badge>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Résumé financier */}
                                  <div className="flex items-center gap-4 mt-2 text-xs">
                                    <span className="text-emerald-600 font-medium">
                                      Payé: <strong>{payeurTotalPaye.toLocaleString('fr-FR')} €</strong>
                                    </span>
                                    {payeurTotalAvoirs > 0 && (
                                      <span className="text-amber-600">Avoir: -{payeurTotalAvoirs.toLocaleString('fr-FR')} €</span>
                                    )}
                                    <span className={`font-medium ${payeurRestantDu > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                      Restant: <strong>{payeurRestantDu.toLocaleString('fr-FR')} €</strong>
                                    </span>
                                  </div>
                                  
                                  {/* Actions facture */}
                                  <div className="flex items-center gap-2 mt-2">
                                    <Button variant="outline" size="sm" className="h-7 text-xs"
                                      onClick={() => handlePreviewFacture(client.prospect_id, devis.devis_id, payeurKey)}
                                      data-testid={`preview-facture-${devis.devis_id}-${payeurKey}`}
                                    >
                                      <Eye className="h-3 w-3 mr-1" /> Voir
                                    </Button>
                                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={async () => {
                                      try {
                                        const response = await axios.get(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/download-facture?payeur_key=${payeurKey}`, { responseType: 'blob' });
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', `${client.nom || ''}_${client.prenom || ''}_facture.pdf`);
                                        document.body.appendChild(link); link.click(); link.remove();
                                        toast.success('Facture téléchargée');
                                      } catch (error) { toast.error('Erreur'); }
                                    }}>
                                      <Download className="h-3 w-3 mr-1" /> PDF
                                    </Button>
                                    <Button variant="outline" size="sm" className="h-7 text-xs text-green-600 border-green-300 hover:bg-green-50" onClick={() => {
                                      setSelectedProspect(client);
                                      setSelectedDevis({...devis, payeur_key: payeurKey, payeur_montant: payeur.montant, payeur_type: payeur.type});
                                      setPaiementData({
                                        montant: payeur.montant - payeurTotalPaye - payeurTotalAvoirs,
                                        date_paiement: new Date().toISOString().split('T')[0],
                                        mode_paiement: 'virement', reference: '', echeance_id: null, payeur_key: payeurKey
                                      });
                                      setShowPaiementDialog(true);
                                    }}>
                                      <CreditCard className="h-3 w-3 mr-1" /> Paiement
                                    </Button>
                                    
                                    {/* Menu secondaire facture */}
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="outline" size="sm" className="h-7 w-7 p-0">
                                          <MoreVertical className="h-3.5 w-3.5" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={async () => {
                                          try {
                                            await axios.post(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/send-facture?payeur_key=${payeurKey}`);
                                            toast.success('Facture envoyée');
                                          } catch (error) { toast.error('Erreur'); }
                                        }}>
                                          <Send className="h-3 w-3 mr-2" /> Envoyer par email
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => {
                                          setSelectedProspect(client);
                                          setSelectedDevis({...devis, payeur_key: payeurKey, payeur_montant: payeur.montant, payeur_type: payeur.type});
                                          setEcheancierCreationData({
                                            nb_echeances: 3,
                                            date_debut: new Date().toISOString().split('T')[0],
                                            montant_total: payeur.montant - payeurTotalAvoirs,
                                            payeur_key: payeurKey
                                          });
                                          setShowEcheancierCreationDialog(true);
                                        }}>
                                          <Calendar className="h-3 w-3 mr-2" /> Créer échéancier
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => {
                                          setSelectedProspect(client);
                                          setSelectedDevis({...devis, payeur_key: payeurKey, payeur_montant: payeur.montant, payeur_type: payeur.type});
                                          setAvoirData({ montant: 0, motif: '', isTotal: false, payeur_key: payeurKey });
                                          setShowAvoirDialog(true);
                                        }}>
                                          <FileOutput className="h-3 w-3 mr-2" /> Avoir partiel
                                        </DropdownMenuItem>
                                        <DropdownMenuItem className="text-amber-600" onClick={async () => {
                                          if (window.confirm(`Avoir total de ${payeur.montant?.toLocaleString('fr-FR')} € ?`)) {
                                            try {
                                              await axios.post(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/generate-avoir`, null, {
                                                params: { montant: payeur.montant, motif: `Avoir total ${payeur.type}`, is_total: true, payeur_key: payeurKey }
                                              });
                                              toast.success('Avoir total généré'); fetchData();
                                            } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
                                          }
                                        }}>
                                          <RotateCcw className="h-3 w-3 mr-2" /> Avoir total
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem className="text-red-600" onClick={async () => {
                                          if (window.confirm(`Supprimer cette facture et ses paiements/avoirs ?`)) {
                                            try {
                                              await axios.delete(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/facture?payeur_key=${payeurKey}`);
                                              toast.success('Facture supprimée'); fetchData();
                                            } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
                                          }
                                        }}>
                                          <Trash className="h-3 w-3 mr-2" /> Supprimer facture
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </div>
                                
                                {/* Paiements */}
                                {payeurPaiements.length > 0 && (
                                  <div className="px-4 py-2 bg-slate-50 border-t border-slate-100">
                                    <p className="text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Paiements</p>
                                    <div className="space-y-1">
                                      {payeurPaiements.map((pmt, pmtIdx) => (
                                        <div key={pmt.paiement_id || pmtIdx} className="flex items-center justify-between bg-white rounded-md px-3 py-1.5 text-xs border border-slate-100">
                                          <div className="flex items-center gap-3">
                                            <span className="text-slate-500 font-medium">{pmt.date_paiement ? new Date(pmt.date_paiement).toLocaleDateString('fr-FR') : '-'}</span>
                                            <Badge variant="outline" className="text-xs h-5">{pmt.mode_paiement || 'Virement'}</Badge>
                                            {pmt.reference && <span className="text-slate-400">Réf: {pmt.reference}</span>}
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="font-bold text-green-600">+{pmt.montant?.toLocaleString('fr-FR')} €</span>
                                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600" onClick={async (e) => {
                                              e.stopPropagation();
                                              if (window.confirm(`Annuler ce paiement de ${pmt.montant?.toLocaleString('fr-FR')} € ?`)) {
                                                try {
                                                  await axios.delete(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/paiement/${pmt.paiement_id}?payeur_key=${payeurKey}`);
                                                  toast.success('Paiement annulé'); fetchData();
                                                } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
                                              }
                                            }}>
                                              <XCircle className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Avoirs */}
                                {payeurAvoirs.length > 0 && (
                                  <div className="px-4 py-2 bg-amber-50/50 border-t border-slate-100">
                                    <p className="text-xs font-semibold text-amber-700 mb-1.5 uppercase tracking-wide">Avoirs</p>
                                    <div className="flex flex-wrap gap-2">
                                      {payeurAvoirs.map((avoir, avoirIdx) => (
                                        <div key={avoir.avoir_id} className="flex items-center gap-1 bg-white px-2 py-1 rounded border border-amber-200 text-xs">
                                          <span className="text-amber-700 font-medium">-{avoir.montant?.toLocaleString('fr-FR')} €</span>
                                          <Button variant="ghost" size="sm" className="h-5 text-xs px-1 text-amber-600"
                                            onClick={() => handlePreviewAvoir(client.prospect_id, devis.devis_id, avoir.avoir_id)}
                                            data-testid={`preview-avoir-${avoir.avoir_id}`}
                                          >
                                            <Eye className="h-3 w-3" />
                                          </Button>
                                          <Button variant="ghost" size="sm" className="h-5 text-xs px-1 text-amber-600" onClick={async () => {
                                            try {
                                              const response = await axios.get(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/download-avoir/${avoir.avoir_id}`, { responseType: 'blob' });
                                              const url = window.URL.createObjectURL(new Blob([response.data]));
                                              const link = document.createElement('a');
                                              link.href = url; link.setAttribute('download', `Avoir_${avoir.avoir_numero}.pdf`);
                                              document.body.appendChild(link); link.click(); link.remove();
                                            } catch (error) { toast.error('Erreur'); }
                                          }}>
                                            <Download className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                  
                                {/* Échéancier */}
                                {devis.echeancier && devis.echeancier.filter(e => e.payeur_key === payeurKey || !e.payeur_key).length > 0 && (
                                  <div className="px-4 py-2 bg-indigo-50/50 border-t border-slate-100">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                                        Échéancier ({devis.echeancier.filter(e => e.payeur_key === payeurKey || !e.payeur_key).length} échéances)
                                      </p>
                                      <div className="flex items-center gap-1">
                                        <Button variant="ghost" size="sm" className="h-5 text-xs px-1 text-indigo-600"
                                          onClick={() => handlePreviewEcheancier(client.prospect_id, devis.devis_id, payeurKey)}
                                          data-testid={`preview-echeancier-${devis.devis_id}-${payeurKey}`}
                                        >
                                          <Eye className="h-3 w-3 mr-1" /> Voir
                                        </Button>
                                        <Button variant="ghost" size="sm" className="h-5 text-xs px-1 text-indigo-600" onClick={async () => {
                                          try {
                                            const response = await axios.get(`/crm/prospects/${client.prospect_id}/devis/${devis.devis_id}/download-echeancier?payeur_key=${payeurKey}`, { responseType: 'blob' });
                                            const url = window.URL.createObjectURL(new Blob([response.data]));
                                            const link = document.createElement('a');
                                            link.href = url;
                                            link.setAttribute('download', `Echeancier_${devis.facture_numero || devis.devis_id}_${payeurKey}.pdf`);
                                            document.body.appendChild(link); link.click(); link.remove();
                                          } catch (error) { toast.error('Erreur'); }
                                        }}>
                                          <Download className="h-3 w-3 mr-1" /> PDF
                                        </Button>
                                      </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {devis.echeancier.filter(e => e.payeur_key === payeurKey || !e.payeur_key).map(ech => (
                                        <div
                                          key={ech.echeance_id}
                                          className={`text-xs px-2.5 py-1 rounded-md ${
                                            ech.statut === 'paye' || ech.statut === 'payee'
                                              ? 'bg-green-700 text-white font-medium' 
                                              : ech.statut === 'en_retard'
                                              ? 'bg-red-100 text-red-700'
                                              : 'bg-white border border-indigo-200 text-indigo-700'
                                          }`}
                                        >
                                          <span className="font-medium">#{ech.numero}</span>
                                          <span className="mx-1">-</span>
                                          <span>{new Date(ech.date_echeance).toLocaleDateString('fr-FR')}</span>
                                          <span className="mx-1">:</span>
                                          <span className="font-medium">{ech.montant?.toLocaleString('fr-FR')} €</span>
                                          {(ech.statut === 'paye' || ech.statut === 'payee') && <span className="ml-1">✓</span>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                        })}
                      </div>
                    );
                  });
                })()}
                </div>
              )}
                </>
              )}
            </Card>
          );
        });
      })()}
    </div>
  </CardContent>
</Card>

      {/* Dialogue de fusion */}
      {mergeSource && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="merge-client-dialog">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Fusionner un contact</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Source : <span className="font-mono bg-slate-100 px-1 rounded">N°{String(mergeSource.numero_candidat || 0).padStart(4, '0')}</span> {mergeSource.prenom} {mergeSource.nom}
                </p>
              </div>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setMergeSource(null); setMergeTarget(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-600 mb-3">Sélectionnez le contact à fusionner. Le <strong>numéro candidat le plus ancien</strong> sera conservé.</p>
              <Input
                placeholder="Rechercher par nom, prénom ou email..."
                value={mergeSearch}
                onChange={(e) => { setMergeSearch(e.target.value); setMergeTarget(null); }}
                className="mb-3"
                data-testid="merge-client-search-input"
                autoFocus
              />
              <div className="max-h-48 overflow-y-auto space-y-1">
                {mergeTargets.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {mergeSearch ? 'Aucun contact trouvé' : 'Tapez pour rechercher un contact'}
                  </p>
                ) : mergeTargets.map(t => (
                  <button
                    key={t.prospect_id}
                    className={`w-full text-left p-2.5 rounded-lg border transition-colors flex items-center justify-between ${
                      mergeTarget?.prospect_id === t.prospect_id
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-300'
                        : 'border-slate-200 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    onClick={() => setMergeTarget(t)}
                    data-testid={`merge-client-target-${t.prospect_id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {t.numero_candidat && (
                          <span className="text-xs font-mono bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded mr-2">N°{String(t.numero_candidat).padStart(4, '0')}</span>
                        )}
                        {t.prenom} {t.nom}
                      </p>
                      <p className="text-xs text-slate-500">{t.email}</p>
                    </div>
                    {mergeTarget?.prospect_id === t.prospect_id ? (
                      <Check className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Merge className="h-4 w-4 text-slate-400" />
                    )}
                  </button>
                ))}
              </div>
              {mergeTarget && (
                <div className="mt-4 pt-3 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-2">
                    Fusionner <strong>{mergeSource.prenom} {mergeSource.nom}</strong> avec <strong>{mergeTarget.prenom} {mergeTarget.nom}</strong>
                  </p>
                  <Button
                    onClick={handleMerge}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="btn-confirm-merge-client"
                  >
                    <Merge className="h-4 w-4 mr-2" />
                    Confirmer la fusion
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
